import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Trash2, 
  Save, 
  Loader2, 
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Youtube,
  BookOpen,
  Edit2,
  X,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  Upload,
  Film
} from 'lucide-react';
import { toast } from 'sonner';
import { TrainingAttachments } from '../../components/training/TrainingAttachments';

const AdminFormazione = ({ canManageAttachments = false }) => {
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(null);
  const [generatingQuiz, setGeneratingQuiz] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [newVideo, setNewVideo] = useState({
    youtube_url: '',
    title: '',
    description: '',
    duration: '',
    video_type: 'youtube'
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedQuiz, setExpandedQuiz] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [uploadingThumb, setUploadingThumb] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/training/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Errore nel caricamento dei video');
    } finally {
      setLoading(false);
    }
  };

  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleYouTubeUrlChange = async (url) => {
    const videoId = extractYouTubeId(url);
    if (!videoId) {
      toast.error('URL YouTube non valido');
      return;
    }
    setNewVideo(prev => ({ ...prev, youtube_url: url, youtube_id: videoId }));
    return videoId;
  };

  const generateDescription = async (videoId, videoIndex = null) => {
    const targetId = videoIndex !== null ? videoIndex : 'new';
    setGeneratingDesc(targetId);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/training/generate-description`,
        { youtube_id: videoId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { title, description, duration } = response.data;
      if (videoIndex !== null) {
        setVideos(prev => prev.map((v, i) => 
          i === videoIndex ? { ...v, title, description, duration } : v
        ));
      } else {
        setNewVideo(prev => ({ ...prev, title, description, duration }));
      }
      toast.success('Descrizione generata con AI!');
    } catch (error) {
      console.error('Error generating description:', error);
      toast.error('Errore nella generazione della descrizione');
    } finally {
      setGeneratingDesc(null);
    }
  };

  const handleVideoFileUpload = async (file) => {
    if (!file) return;
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = `upload_${Date.now()}`;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('hw_token');
      let finalResult = null;
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('chunk_index', i);
        formData.append('total_chunks', totalChunks);
        formData.append('upload_id', uploadId);
        formData.append('filename', file.name);
        
        const response = await axios.post(`${API}/api/training/upload-video`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
        
        setUploadProgress(Math.round((i + 1) / totalChunks * 100));
        
        if (response.data.complete) {
          finalResult = response.data;
        }
      }
      
      if (finalResult) {
        setUploadedVideoUrl(finalResult.video_url);
        toast.success('Video caricato!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Errore durante il caricamento del video');
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine (JPG, PNG, WebP)');
      return;
    }
    setUploadingThumb(true);
    try {
      const token = localStorage.getItem('hw_token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/api/training/upload-thumbnail`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setThumbnailUrl(response.data.thumbnail_url);
        toast.success('Copertina caricata!');
      }
    } catch (error) {
      console.error('Thumbnail upload error:', error);
      toast.error('Errore nel caricamento della copertina');
    } finally {
      setUploadingThumb(false);
    }
  };

  const addVideo = async () => {
    if (newVideo.video_type === 'youtube') {
      if (!newVideo.youtube_url || !newVideo.title) {
        toast.error('Inserisci URL YouTube e titolo');
        return;
      }
      const videoId = extractYouTubeId(newVideo.youtube_url);
      if (!videoId) {
        toast.error('URL YouTube non valido');
        return;
      }
      setSaving(true);
      try {
        const token = localStorage.getItem('hw_token');
        const videoData = {
          id: `video-${Date.now()}`,
          youtube_id: videoId,
          video_type: 'youtube',
          title: newVideo.title,
          description: newVideo.description || '',
          duration: newVideo.duration || '',
          order: videos.length + 1,
          has_quiz: false
        };
        await axios.post(`${API}/api/training/videos`, videoData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video aggiunto!');
        setNewVideo({ youtube_url: '', title: '', description: '', duration: '', video_type: 'youtube' });
        setShowAddForm(false);
        fetchVideos();
      } catch (error) {
        toast.error("Errore nell'aggiunta del video");
      } finally {
        setSaving(false);
      }
    } else {
      // Upload type
      if (!uploadedVideoUrl || !newVideo.title) {
        toast.error('Carica un video e inserisci il titolo');
        return;
      }
      setSaving(true);
      try {
        const token = localStorage.getItem('hw_token');
        const videoData = {
          id: `video-${Date.now()}`,
          video_type: 'upload',
          video_file_url: uploadedVideoUrl,
          thumbnail: thumbnailUrl || null,
          title: newVideo.title,
          description: newVideo.description || '',
          duration: newVideo.duration || '',
          order: videos.length + 1,
          has_quiz: false
        };
        await axios.post(`${API}/api/training/videos`, videoData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Video aggiunto!');
        setNewVideo({ youtube_url: '', title: '', description: '', duration: '', video_type: 'youtube' });
        setUploadedVideoUrl(null);
        setThumbnailUrl(null);
        setShowAddForm(false);
        fetchVideos();
      } catch (error) {
        toast.error("Errore nell'aggiunta del video");
      } finally {
        setSaving(false);
      }
    }
  };

  const updateVideo = async (video) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.put(`${API}/api/training/videos/${video.id}`, video, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video aggiornato!');
      setEditingVideo(null);
      fetchVideos();
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo video e il relativo quiz?')) return;
    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/api/training/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video eliminato!');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const moveVideo = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= videos.length) return;
    
    setReordering(true);
    const newVideos = [...videos];
    const temp = newVideos[index];
    newVideos[index] = newVideos[newIndex];
    newVideos[newIndex] = temp;
    
    // Aggiorna ordini
    const orders = newVideos.map((v, i) => ({ id: v.id, order: i + 1 }));
    setVideos(newVideos);
    
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/training/videos/reorder`, { orders }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ordine aggiornato');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error("Errore nel riordinamento");
      fetchVideos(); // Ripristina ordine originale
    } finally {
      setReordering(false);
    }
  };

  const generateQuiz = async (video, index) => {
    setGeneratingQuiz(index);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/api/training/generate-quiz`, { video_id: video.id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(prev => prev.map((v, i) => 
        i === index ? { ...v, pending_quiz: response.data.questions } : v
      ));
      setExpandedQuiz(index);
      toast.success('Quiz generato! Rivedi e approva le domande.');
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast.error('Errore nella generazione del quiz');
    } finally {
      setGeneratingQuiz(null);
    }
  };

  const approveQuiz = async (video, index) => {
    if (!video.pending_quiz || video.pending_quiz.length !== 5) {
      toast.error('Il quiz deve avere 5 domande');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/training/approve-quiz`, {
        video_id: video.id,
        questions: video.pending_quiz
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Quiz approvato e salvato!');
      setExpandedQuiz(null);
      fetchVideos();
    } catch (error) {
      console.error('Error approving quiz:', error);
      toast.error("Errore nell'approvazione del quiz");
    } finally {
      setSaving(false);
    }
  };

  const loadExistingQuiz = async (video, index) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/training/admin/quiz/${video.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.questions && response.data.questions.length > 0) {
        setVideos(prev => prev.map((v, i) => 
          i === index ? { ...v, pending_quiz: response.data.questions } : v
        ));
        setExpandedQuiz(index);
        toast.success('Quiz caricato per la modifica');
      } else {
        toast.error('Nessun quiz trovato per questo video');
      }
    } catch (error) {
      console.error('Error loading quiz:', error);
      toast.error('Errore nel caricamento del quiz');
    }
  };

  const deleteQuiz = async (video, index) => {
    if (!window.confirm('Sei sicuro di voler eliminare il quiz da questa lezione?')) return;
    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/api/training/quiz/${video.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quiz eliminato!');
      setExpandedQuiz(null);
      fetchVideos();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error("Errore nell'eliminazione del quiz");
    }
  };

  const updateQuizQuestion = (videoIndex, questionIndex, field, value) => {
    setVideos(prev => prev.map((v, i) => {
      if (i !== videoIndex) return v;
      const newQuiz = [...(v.pending_quiz || [])];
      if (field === 'correct_answer') {
        newQuiz[questionIndex] = { ...newQuiz[questionIndex], correct_answer: parseInt(value) };
      } else if (field === 'question') {
        newQuiz[questionIndex] = { ...newQuiz[questionIndex], question: value };
      } else if (field.startsWith('option_')) {
        const optIndex = parseInt(field.split('_')[1]);
        const newOptions = [...newQuiz[questionIndex].options];
        newOptions[optIndex] = value;
        newQuiz[questionIndex] = { ...newQuiz[questionIndex], options: newOptions };
      }
      return { ...v, pending_quiz: newQuiz };
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        <span className="ml-3 text-slate-600">Caricamento...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-formazione">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-slate-500">{videos.length} {videos.length === 1 ? 'lezione' : 'lezioni'} disponibili</p>
        <Button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-lesson-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Aggiungi Lezione
        </Button>
      </div>

      {/* Add New Video Form */}
      {showAddForm && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {newVideo.video_type === 'youtube' ? <Youtube className="h-5 w-5 text-red-500" /> : <Upload className="h-5 w-5 text-blue-500" />}
              Nuova Lezione
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Video Type Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo di Video</label>
              <div className="flex gap-2">
                <Button
                  variant={newVideo.video_type === 'youtube' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setNewVideo(prev => ({ ...prev, video_type: 'youtube' })); setUploadedVideoUrl(null); setUploadProgress(0); setThumbnailUrl(null); }}
                  className={newVideo.video_type === 'youtube' ? 'bg-red-600 hover:bg-red-700' : ''}
                  data-testid="video-type-youtube"
                >
                  <Youtube className="h-4 w-4 mr-2" />
                  YouTube
                </Button>
                <Button
                  variant={newVideo.video_type === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewVideo(prev => ({ ...prev, video_type: 'upload' }))}
                  className={newVideo.video_type === 'upload' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  data-testid="video-type-upload"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Carica File
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {newVideo.video_type === 'youtube' ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Link YouTube *</label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="https://youtube.com/watch?v=..."
                      value={newVideo.youtube_url}
                      onChange={(e) => handleYouTubeUrlChange(e.target.value)}
                      className="flex-1"
                      data-testid="youtube-url-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        const videoId = extractYouTubeId(newVideo.youtube_url);
                        if (videoId) generateDescription(videoId);
                      }}
                      disabled={!newVideo.youtube_url || generatingDesc === 'new'}
                    >
                      {generatingDesc === 'new' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">Clicca il bottone per generare titolo e descrizione con AI</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium">File Video *</label>
                  {!uploadedVideoUrl ? (
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleVideoFileUpload(e.target.files[0])}
                        disabled={uploading}
                        data-testid="video-file-input"
                      />
                      {uploading && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-blue-600 font-medium">Caricamento in corso...</span>
                            <span className="font-bold">{uploadProgress}%</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-slate-500">Formati: MP4, AVI, MOV. Il file viene caricato in blocchi.</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">Video caricato</span>
                      <Button variant="ghost" size="sm" onClick={() => setUploadedVideoUrl(null)} className="ml-auto text-red-500 hover:text-red-700">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium">Titolo *</label>
                <Input
                  placeholder="Titolo della lezione"
                  value={newVideo.title}
                  onChange={(e) => setNewVideo(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="video-title-input"
                />
              </div>
            </div>

            {/* Thumbnail upload - solo per video caricati */}
            {newVideo.video_type === 'upload' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Copertina (Thumbnail)</label>
                <div className="flex items-start gap-4">
                  {thumbnailUrl ? (
                    <div className="relative group">
                      <img src={thumbnailUrl} alt="Copertina" className="w-44 h-28 object-cover rounded-lg border border-slate-200 shadow-sm" />
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setThumbnailUrl(null)} 
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-500 text-white hover:bg-red-600 shadow"
                        data-testid="remove-thumbnail-btn"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleThumbnailUpload(e.target.files[0])}
                          disabled={uploadingThumb}
                          className="max-w-xs"
                          data-testid="thumbnail-file-input"
                        />
                        {uploadingThumb && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                      </div>
                      <p className="text-xs text-slate-500">Immagine di copertina (JPG, PNG, WebP). Consigliato: 16:9</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Descrizione</label>
                <Textarea
                  placeholder="Descrizione della lezione"
                  value={newVideo.description}
                  onChange={(e) => setNewVideo(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Durata (opzionale)</label>
                <Input
                  placeholder="es. 15:30"
                  value={newVideo.duration}
                  onChange={(e) => setNewVideo(prev => ({ ...prev, duration: e.target.value }))}
                  data-testid="video-duration-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowAddForm(false); setUploadedVideoUrl(null); setUploadProgress(0); setThumbnailUrl(null); }}>Annulla</Button>
              <Button onClick={addVideo} disabled={saving || (newVideo.video_type === 'upload' && uploading)} data-testid="save-lesson-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Salva Lezione
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Videos List */}
      <div className="space-y-4">
        {videos.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-slate-500">
              <BookOpen className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-lg font-medium">Nessuna lezione</p>
              <p className="text-sm">Clicca "Aggiungi Lezione" per iniziare</p>
            </CardContent>
          </Card>
        ) : (
          videos.map((video, index) => (
            <Card key={video.id} className="overflow-hidden" data-testid={`video-card-${index}`}>
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Reorder Buttons */}
                  <div className="flex md:flex-col justify-center items-center bg-slate-50 px-2 py-1 md:py-0 gap-1 border-b md:border-b-0 md:border-r">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => moveVideo(index, -1)}
                      disabled={index === 0 || reordering}
                      title="Sposta su"
                      data-testid={`move-up-${index}`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <span className="text-xs font-bold text-slate-400">{index + 1}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => moveVideo(index, 1)}
                      disabled={index === videos.length - 1 || reordering}
                      title="Sposta giù"
                      data-testid={`move-down-${index}`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Thumbnail */}
                  <div className="hidden md:block w-44 h-28 flex-shrink-0 bg-slate-100 relative">
                    {video.thumbnail ? (
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : video.video_type === 'upload' ? (
                      <div className="w-full h-full flex items-center justify-center bg-slate-200">
                        <Film className="h-10 w-10 text-slate-400" />
                      </div>
                    ) : (
                      <img
                        src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {video.video_type === 'upload' && (
                      <Badge className="absolute bottom-1 left-1 bg-blue-600 text-white text-xs">Upload</Badge>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 p-4">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {editingVideo === index ? (
                          <Input
                            value={video.title}
                            onChange={(e) => setVideos(prev => prev.map((v, i) => 
                              i === index ? { ...v, title: e.target.value } : v
                            ))}
                            className="font-bold text-lg mb-2"
                          />
                        ) : (
                          <h3 className="font-bold text-base text-slate-800">{video.title}</h3>
                        )}
                        
                        {editingVideo === index ? (
                          <Textarea
                            value={video.description}
                            onChange={(e) => setVideos(prev => prev.map((v, i) => 
                              i === index ? { ...v, description: e.target.value } : v
                            ))}
                            rows={2}
                            className="text-sm"
                          />
                        ) : (
                          <p className="text-sm text-slate-600 line-clamp-2">{video.description}</p>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                          {video.youtube_id && <span className="text-xs text-slate-400">ID: {video.youtube_id}</span>}
                          {video.video_type === 'upload' && <Badge variant="outline" className="text-xs text-blue-600 border-blue-300"><Upload className="h-3 w-3 mr-1" />File caricato</Badge>}
                          {video.duration && (
                            <span className="text-xs text-slate-400">Durata: {video.duration}</span>
                          )}
                          {video.has_quiz ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Quiz Pronto
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Quiz Mancante
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-2 sm:ml-4">
                        {editingVideo === index ? (
                          <>
                            <Button size="sm" onClick={() => updateVideo(video)} disabled={saving}>
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingVideo(null); fetchVideos(); }}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => generateDescription(video.youtube_id, index)} disabled={generatingDesc === index} title="Rigenera descrizione con AI">
                              {generatingDesc === index ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingVideo(index)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteVideo(video.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    <TrainingAttachments video={video} admin={canManageAttachments} onChanged={fetchVideos} />

                    {/* Quiz Section */}
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <span className="text-sm font-medium text-slate-700">Quiz di Verifica</span>
                        <div className="flex flex-wrap gap-2">
                          {video.has_quiz && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => loadExistingQuiz(video, index)}
                                title="Modifica quiz esistente"
                              >
                                <Edit2 className="h-4 w-4 mr-1" />
                                Modifica
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => deleteQuiz(video, index)}
                                title="Elimina quiz"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Elimina Quiz
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant={video.has_quiz ? "outline" : "default"}
                            onClick={() => generateQuiz(video, index)}
                            disabled={generatingQuiz === index}
                            className={!video.has_quiz ? "bg-purple-600 hover:bg-purple-700" : ""}
                          >
                            {generatingQuiz === index ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                            {video.has_quiz ? 'Rigenera Quiz' : 'Genera Quiz con AI'}
                          </Button>
                          {(video.pending_quiz || expandedQuiz === index) && (
                            <Button size="sm" variant="ghost" onClick={() => setExpandedQuiz(expandedQuiz === index ? null : index)}>
                              {expandedQuiz === index ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Quiz Editor */}
                      {expandedQuiz === index && video.pending_quiz && (
                        <div className="mt-4 space-y-4 bg-slate-50 rounded-lg p-4">
                          <p className="text-sm text-slate-600 mb-4">
                            Rivedi le domande generate dall'AI. Puoi modificarle prima di approvare.
                          </p>
                          {video.pending_quiz.map((q, qIndex) => (
                            <div key={qIndex} className="bg-white p-4 rounded-lg border">
                              <div className="flex items-start gap-2 mb-3">
                                <Badge variant="outline">{qIndex + 1}</Badge>
                                <Input
                                  value={q.question}
                                  onChange={(e) => updateQuizQuestion(index, qIndex, 'question', e.target.value)}
                                  className="flex-1 font-medium"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                {q.options.map((opt, optIndex) => (
                                  <div key={optIndex} className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name={`correct-${index}-${qIndex}`}
                                      checked={q.correct_answer === optIndex}
                                      onChange={() => updateQuizQuestion(index, qIndex, 'correct_answer', optIndex)}
                                      className="text-green-600"
                                    />
                                    <Input
                                      value={opt}
                                      onChange={(e) => updateQuizQuestion(index, qIndex, `option_${optIndex}`, e.target.value)}
                                      className={q.correct_answer === optIndex ? 'border-green-500 bg-green-50' : ''}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => { setExpandedQuiz(null); fetchVideos(); }}>
                              Annulla
                            </Button>
                            <Button
                              onClick={() => approveQuiz(video, index)}
                              disabled={saving}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                              Approva Quiz
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminFormazione;
